// React & Hooks
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";

// Helpers
import { useTranslate } from "helpers";

// Navigacija
import { Routes as MarketplaceRoutes } from "../../navigation";

// API
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { getListingByVehicleAPI } from "services/api/sales/listings/api";
import { VehicleListing } from "services/api/sales/listings/types";

// API Tipovi & Konstante
import { FleetVehicleDetailFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleDetailForm } from "services/api/fleet/vehicles/consts";

// UI Komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Loader } from "ui/components/Loader";

// Lokalne komponente
import VehicleGalleryPreview from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleInfoCard/components/VehicleGalleryPreview/VehicleGalleryPreview";
import { VehicleInfoCard, VehicleSpecsCard, DealerCard, AdditionalInfoCard } from "./components";
import { Section } from "./components/primitives";

// Sekcije oglasa se dijele s detaljem oglasa — isti prikaz istih podataka.
import ListingPayment from "ui/modules/sales/pages/listings/ListingDetailPage/components/ListingSpecification/components/ListingPayment";
import ListingWarranty from "ui/modules/sales/pages/listings/ListingDetailPage/components/ListingSpecification/components/ListingWarranty";
import ListingEquipment from "ui/modules/sales/pages/listings/ListingDetailPage/components/ListingSpecification/components/ListingEquipment";

// Tipovi
import { MarketplaceDetailPageCmp, MarketplaceDetailPageProps } from "./types";

// Konstante
import { MARKETPLACE_DETAIL_PAGE_TITLE, MARKETPLACE_PAGE_TITLE, MARKETPLACE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

const MarketplaceDetailPage: MarketplaceDetailPageCmp = (
  props: MarketplaceDetailPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FleetVehicleDetailFormValues>(
    initialFleetVehicleDetailForm
  );
  // Oglas vozila — iz njega dolaze opis, plaćanje, jamstvo, lokacija, oprema i video.
  const [listing, setListing] = useState<VehicleListing | null>(null);

  useEffect(() => {
    if (id) fetchVehicle(id);
  }, [id]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    // Vozilo bez oglasa i dalje prikazuje specifikacije — zato tihi catch.
    const listingResult = await getListingByVehicleAPI(id).catch(() => null);

    setFormValues(result);
    setListing(listingResult);
    setIsLoading(false);
  };


  const title = MARKETPLACE_DETAIL_PAGE_TITLE;

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: MARKETPLACE_PAGE_TITLE.text,
      route: MARKETPLACE_SEARCH_PAGE_TITLE.route,
    },
  ];

  const marketplaceDetails = listing?.marketplaceDetails ?? null;
  // Opis: marketplace kanal ima prednost pred osnovnim tekstom oglasa.
  const marketplaceChannel = listing?.channels.find((c) => c.salesChannelCode === "marketplace");
  const description = marketplaceChannel?.effectiveDescription
    || marketplaceChannel?.descriptionOverride
    || listing?.baseDescription
    || "";

  const vehicleTitle = `${formValues.vehicleManufacturer?.name || ""} ${formValues.vehicleModel?.name || ""}`.trim();
  const vehicleYear = formValues.firstRegistrationDate ? new Date(formValues.firstRegistrationDate).getFullYear() : formValues.vehicleModel?.year;

  if (!id) return <></>;
  if (isLoading) return <Loader isLoading={true}>{null}</Loader>;

  return (
    <>
      <div>
        {/* Header — isti stil kao detalj oglasa (bijela kartica + tab) */}
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div className="flex gap-3 items-start">
                {formValues.defaultImage && (
                  <img
                    src={formValues.defaultImage}
                    alt={formValues.name ?? ""}
                    title={formValues.name ?? ""}
                    className="h-12 w-12 object-contain rounded-full"
                  />
                )}
                <div>
                  <PageTitle
                    title={`${t(title.text)} ${formValues.name ? `[${formValues.name}]` : ""}`}
                    icon={title.icon}
                    iconColor={title.iconColor}
                  />
                  <Breadcrumb items={breadcrumbs} />
                </div>
              </div>
            </div>
          </PageHeader>

          {/* Statični tab — Detalji oglasa */}
          <div className="flex">
            <div className="group flex items-center gap-2 font-semibold pl-2 pr-4 py-2 ml-3 whitespace-nowrap border-b-2 text-sky-600 border-sky-600">
              <ReactSVG src="/media/icons/info.svg" className="w-4 h-4 [&_svg]:w-4 [&_svg]:h-4 [&_path]:stroke-sky-600" />
              Detalji oglasa
            </div>
          </div>
        </div>

        {/* Sadržaj — oglas u sredini, kontakt u sticky stupcu desno. */}
        <div className="px-0 pt-2 flex justify-center gap-4">
          <div className="w-1/2 flex flex-col gap-4">
            <div className="bg-white rounded-sm border">
              {/* Video oglasa ulazi u galeriju kao prva pločica — fleet detalj ga ne šalje. */}
              <VehicleGalleryPreview
                fleetVehicleDetail={formValues}
                videoUrl={marketplaceDetails?.youtubeUrl}
              />

              {/* Redoslijed sekcija prati detalj oglasa: naslov → opis → plaćanje →
                  specifikacije → ostali podaci → jamstvo → lokacija → oprema → video. */}
              <VehicleInfoCard
                formValues={formValues}
                vehicleTitle={vehicleTitle}
                vehicleYear={vehicleYear}
              />
              {description && (
                <Section title={t("admin.sales.listing.detail.description")}>
                  <div className="text-sm text-slate-700 whitespace-pre-line mb-3">{description}</div>
                </Section>
              )}
              <VehicleSpecsCard formValues={formValues} />
              <AdditionalInfoCard formValues={formValues} details={marketplaceDetails} />
              <ListingWarranty details={marketplaceDetails} />
              {/* Lokacija je u desnom stupcu, video u galeriji — ovdje se ne ponavljaju. */}
              <ListingEquipment details={marketplaceDetails} />
            </div>
          </div>

          {/* Kontakt i plaćanje prate skrol — kupac ih ima nadohvat kroz cijeli oglas. */}
          <div className="w-1/4 hidden lg:block">
            <div className="sticky top-4 flex flex-col gap-4">
              {/* `[&>div]:border-b-0` gasi crtu sekcije — u vlastitoj kartici nema što odvajati. */}
              <div className="bg-white rounded-sm border [&>div]:border-b-0">
                <DealerCard
                  contacts={listing?.contacts}
                  offices={listing?.offices}
                  company={listing?.company}
                  details={marketplaceDetails}
                />
              </div>
              <div className="bg-white rounded-sm border [&>div]:border-b-0">
                <ListingPayment details={marketplaceDetails} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

MarketplaceDetailPage.route = MarketplaceRoutes.MarketplaceDetailPageRouter;

export default MarketplaceDetailPage;
